<template>
  <section class="learning-welcome" :class="`learning-welcome--${model.state}`" aria-live="polite">
    <div class="learning-welcome-icon" aria-hidden="true">
      <i class="el-icon-s-custom" />
    </div>
    <div v-if="loading" class="learning-welcome-main learning-welcome-loading">
      <span class="skeleton skeleton-title" />
      <span class="skeleton skeleton-line" />
      <span class="loading-label">{{ $t('chat.learningWelcome.loading') }}</span>
    </div>
    <div v-else class="learning-welcome-main">
      <h3>{{ $t(model.titleKey, { name: displayName }) }}</h3>
      <p>{{ $t(model.bodyKey, model.params) }}</p>
      <p v-if="model.nextKey" class="learning-welcome-next">
        <i class="el-icon-right" aria-hidden="true" />
        {{ $t(model.nextKey, model.params) }}
      </p>
      <div v-if="model.showProgress" class="learning-welcome-progress" :aria-label="`${model.percentage}%`">
        <span :style="{ width: `${model.percentage}%` }" />
      </div>

      <div v-if="model.previousLearning" class="learning-welcome-block">
        <h4>{{ $t('chat.learningWelcome.previousTitle') }}</h4>
        <p>{{ $t('chat.learningWelcome.lastStudied', {
          knowledgePointName: model.previousLearning.knowledgePointName
        }) }}</p>

        <div v-if="model.previousLearning.learnedItems.length" class="learning-welcome-fact">
          <span class="learning-welcome-label">{{ $t('chat.learningWelcome.learnedItems') }}</span>
          <ul>
            <li
              v-for="item in model.previousLearning.learnedItems"
              :key="`learned-${item.id}`"
              :class="{ 'learning-welcome-stage-item': stageMeta(item) }"
            >
              <strong>{{ stageTitle(item) }}</strong>
              <span v-if="stageDescription(item)" class="learning-welcome-stage-description">
                {{ stageDescription(item) }}
              </span>
            </li>
          </ul>
        </div>

        <div v-if="model.previousLearning.masteredItems.length" class="learning-welcome-fact learning-welcome-fact--mastered">
          <span class="learning-welcome-label">{{ $t('chat.learningWelcome.masteredItems') }}</span>
          <ul>
            <li
              v-for="item in model.previousLearning.masteredItems"
              :key="`mastered-${item.id}`"
              :class="{ 'learning-welcome-stage-item': stageMeta(item) }"
            >
              <strong>{{ stageTitle(item) }}</strong>
              <span v-if="stageDescription(item)" class="learning-welcome-stage-description">
                {{ stageDescription(item) }}
              </span>
            </li>
          </ul>
        </div>

        <div v-if="model.previousLearning.needsPracticeItems.length" class="learning-welcome-fact learning-welcome-fact--practice">
          <span class="learning-welcome-label">{{ $t('chat.learningWelcome.needsPracticeItems') }}</span>
          <ul>
            <li
              v-for="item in model.previousLearning.needsPracticeItems"
              :key="`practice-${item.id}`"
              :class="{ 'learning-welcome-stage-item': stageMeta(item) }"
            >
              <strong>{{ stageTitle(item) }}</strong>
              <span v-if="stageDescription(item)" class="learning-welcome-stage-description">
                {{ stageDescription(item) }}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div v-if="model.todayPlan" class="learning-welcome-block">
        <h4>{{ $t('chat.learningWelcome.todayPlanTitle') }}</h4>
        <p><strong>{{ $t('chat.learningWelcome.goal') }}:</strong> {{ model.todayPlan.goal }}</p>
        <p v-if="model.todayPlan.reason" class="learning-welcome-plan-reason">
          <strong>{{ $t('chat.learningWelcome.reason') }}:</strong> {{ model.todayPlan.reason }}
        </p>
        <ul v-if="model.todayPlan.steps.length" class="learning-welcome-steps">
          <li v-for="step in model.todayPlan.steps" :key="`plan-${step.id}`">
            <div class="learning-welcome-step-heading">
              <strong>{{ stageTitle(step) }}</strong>
              <small v-if="step.estimatedMinutes !== null">
                {{ $t('chat.learningWelcome.minutes', { minutes: step.estimatedMinutes }) }}
              </small>
            </div>
            <p v-if="stageDescription(step)" class="learning-welcome-stage-description">
              {{ stageDescription(step) }}
            </p>
          </li>
        </ul>
        <p v-if="model.todayPlan.totalEstimatedMinutes !== null" class="learning-welcome-total-time">
          <i class="el-icon-time" aria-hidden="true" />
          {{ $t('chat.learningWelcome.totalTime', { minutes: model.todayPlan.totalEstimatedMinutes }) }}
        </p>
      </div>
    </div>
  </section>
</template>

<script>
import { buildLearningWelcomeModel } from '@/utils/learningContext.mjs'

const STAGE_META_BY_NAME = {
  understand: { number: 1, key: 'understand' },
  apply: { number: 2, key: 'apply' },
  master: { number: 3, key: 'master' },
  evaluate: { number: 4, key: 'evaluate' }
}

export default {
  name: 'LearningWelcomeCard',
  props: {
    context: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    studentName: {
      type: String,
      default: ''
    }
  },
  computed: {
    model () {
      return buildLearningWelcomeModel(this.error ? null : this.context)
    },
    displayName () {
      return this.studentName.trim() || this.$t('chat.learningWelcome.studentFallback')
    }
  },
  methods: {
    stageMeta (item) {
      if (!item || item.type !== 'STAGE') return null
      return STAGE_META_BY_NAME[String(item.name || '').trim().toLowerCase()] || null
    },
    stageTitle (item) {
      const stage = this.stageMeta(item)
      if (!stage) return item.name
      return this.$t('chat.learningWelcome.stageTitle', {
        number: stage.number,
        name: item.name
      })
    },
    stageDescription (item) {
      const stage = this.stageMeta(item)
      return stage
        ? this.$t(`chat.learningWelcome.stageDescriptions.${stage.key}`)
        : ''
    }
  }
}
</script>

<style scoped>
.learning-welcome {
  display: flex;
  gap: 12px;
  margin: 4px 4px 20px;
  padding: 18px;
  border: 1px solid rgba(124, 58, 237, 0.16);
  border-radius: 18px;
  color: #334155;
  background: linear-gradient(135deg, #faf5ff 0%, #ffffff 58%, #f5f3ff 100%);
  box-shadow: 0 10px 30px rgba(91, 33, 182, 0.08);
}

.learning-welcome-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  border-radius: 13px;
  color: #fff;
  font-size: 20px;
  background: linear-gradient(135deg, #6d28d9, #8b5cf6);
}

.learning-welcome-main {
  min-width: 0;
  flex: 1;
}

.learning-welcome h3 {
  margin: 0 0 8px;
  color: #4c1d95;
  font-size: 18px;
  line-height: 1.3;
}

.learning-welcome p {
  margin: 0;
  font-size: 15px;
  line-height: 1.55;
}

.learning-welcome-next {
  margin-top: 8px !important;
  color: #6d28d9;
  font-weight: 600;
}

.learning-welcome-progress {
  height: 6px;
  margin-top: 14px;
  overflow: hidden;
  border-radius: 999px;
  background: #ede9fe;
}

.learning-welcome-progress span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #7c3aed, #a855f7);
  transition: width 0.25s ease;
}

.learning-welcome-block {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #ede9fe;
}

.learning-welcome-block h4 {
  margin: 0 0 8px;
  color: #5b21b6;
  font-size: 15px;
}

.learning-welcome-fact {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 9px;
}

.learning-welcome-label {
  flex: 0 0 auto;
  padding-top: 3px;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}

.learning-welcome-fact ul {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.learning-welcome-fact li {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 3px 8px;
  border-radius: 999px;
  color: #475569;
  font-size: 12px;
  background: #f1f5f9;
}

.learning-welcome-fact .learning-welcome-stage-item {
  flex: 1 1 100%;
  padding: 8px 10px;
  border-radius: 10px;
}

.learning-welcome-fact--mastered li {
  color: #047857;
  background: #ecfdf5;
}

.learning-welcome-fact--practice li {
  color: #c2410c;
  background: #fff7ed;
}

.learning-welcome-plan-reason {
  margin-top: 5px !important;
  color: #64748b;
}

.learning-welcome-steps {
  display: grid;
  gap: 8px;
  margin: 10px 0 0;
  padding: 0;
  list-style: none;
}

.learning-welcome-steps li {
  padding: 9px 11px;
  border: 1px solid #ede9fe;
  border-radius: 10px;
  color: #475569;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.72);
}

.learning-welcome-step-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.learning-welcome-steps small {
  flex: 0 0 auto;
  color: #94a3b8;
}

.learning-welcome-stage-description {
  color: #64748b;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
}

.learning-welcome-steps .learning-welcome-stage-description {
  margin-top: 4px;
  font-size: 13px;
}

.learning-welcome-total-time {
  margin-top: 8px !important;
  color: #6d28d9;
  font-size: 13px !important;
  font-weight: 600;
}

.learning-welcome-loading {
  position: relative;
  padding-top: 2px;
}

.skeleton {
  display: block;
  border-radius: 999px;
  background: linear-gradient(90deg, #ede9fe 25%, #faf5ff 50%, #ede9fe 75%);
  background-size: 200% 100%;
  animation: welcome-shimmer 1.25s infinite;
}

.skeleton-title {
  width: 42%;
  height: 16px;
}

.skeleton-line {
  width: 78%;
  height: 12px;
  margin-top: 12px;
}

.loading-label {
  display: inline-block;
  margin-top: 10px;
  color: #7c3aed;
  font-size: 13px;
}

@keyframes welcome-shimmer {
  to { background-position: -200% 0; }
}

@media (max-width: 768px) {
  .learning-welcome {
    padding: 14px;
  }
}
</style>
